import React, { useState, useEffect } from 'react';
import { ChevronUp } from 'lucide-react';

/**
 * Floating scroll-to-top button.
 *
 * - Fades in after the user scrolls down 2× the viewport height.
 * - On mobile (screen height < 768px) sits above the bottom nav bar (bottom: 80px).
 * - Smooth scrolls to the very top on click.
 * - Respects prefers-reduced-motion: skips animation when motion is reduced.
 * - z-index 40 keeps it above page content but below modals (z-50).
 */
const SCROLL_THRESHOLD_VH = 2; // show after 2 viewport heights
const TRANSITION_MS = 200;

const ScrollToTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const threshold = window.innerHeight * SCROLL_THRESHOLD_VH;
      setVisible(window.scrollY > threshold);
    };

    // Passive listener for better scroll perf
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      type="button"
      aria-label="Scroll to top"
      onClick={scrollToTop}
      style={{
        position: 'fixed',
        right: '20px',
        // On small screens sit above the mobile bottom nav (64px height + 16px gap)
        bottom: 'clamp(80px, calc(64px + 16px), 80px)',
        width: '40px',
        height: '40px',
        zIndex: 40,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: `opacity ${TRANSITION_MS}ms ease`,
        // Semi-transparent surface that respects the current theme
        background: 'hsl(var(--foreground) / 0.12)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: '1px solid hsl(var(--border))',
        borderRadius: '9999px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'hsl(var(--foreground))',
      }}
      // Lifted bottom on desktop so it doesn't overlap the footer CTA area
      className="lg:bottom-6"
    >
      <ChevronUp
        aria-hidden="true"
        style={{ width: '18px', height: '18px', flexShrink: 0 }}
      />
    </button>
  );
};

export default ScrollToTopButton;
